export interface V2Locale {
  /**
   * Locale ID.
   *
   * The locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.
   * @maxLength 20
   * @format LANGUAGE_TAG
   * @readonly
   * @immutable
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the locale was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the locale was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Language code. For example, `"en"` for English.
   * @immutable
   * @format LANGUAGE
   */
  languageCode?: string | null;
  /**
   * Region code. For example, `"UK"` for United Kingdom.
   * @immutable
   * @format COUNTRY
   */
  regionCode?: string | null;
  /** Locale visibility status. */
  visibility?: LocaleVisibilityWithLiterals;
  /**
   * Whether the locale is the site's primary locale.
   * @readonly
   */
  primaryLocale?: boolean | null;
  /**
   * Whether the locale is the site's visitor primary locale.
   * @readonly
   */
  visitorPrimaryLocale?: boolean | null;
  /**
   * Flag icon as a 3-letter language code based on Wix's supported locales. For example, `"USA"` for the United States flag icon.
   * @maxLength 3
   */
  flag?: string | null;
  /**
   * Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.
   * @format LANGUAGE_TAG
   */
  regionalFormat?: string | null;
  /**
   * Default URL structure for the locale's live site URL,
   * based on the locale's settings, unless overridden.
   * @readonly
   */
  effectiveUrlStructure?: UrlStructureWithLiterals;
  /** Optional override for the locale's default URL structure. */
  urlStructureOverride?: UrlStructureOverride;
  /**
   * Language code based on Wix's supported locales, used to specify the target language when
   * translatable content is sent for machine translation. For example, `"ES"` for Spanish
   * or `"ZH_TW"` for Traditional Chinese.
   *
   * Default: The locale's `languageCode` value.
   * @format LANGUAGE_TAG
   */
  machineTranslationCode?: string | null;
  /**
   * The locale's `displayName`, unless overridden.
   * @readonly
   * @maxLength 30
   */
  effectiveDisplayName?: string | null;
  /**
   * Default display name for the locale, inferred from the locale ID and region code.
   * @readonly
   * @maxLength 30
   */
  displayName?: string | null;
  /**
   * Optional override for the locale's display name.
   * @maxLength 30
   */
  overrideDisplayName?: string | null;
}

/** Enum representing the visibility status of a locale. */
export enum LocaleVisibility {
  /** Locale is hidden from site visitors. A site's locale should be hidden when a translation is not ready to be exposed to visitors. */
  HIDDEN = 'HIDDEN',
  /** Locale is visible to site visitors. A site can only have up to 100 visible locales. */
  VISIBLE = 'VISIBLE',
}

/** @enumType */
export type LocaleVisibilityWithLiterals =
  | LocaleVisibility
  | 'HIDDEN'
  | 'VISIBLE';

/** Enum representing the method for resolving locales in URLs. */
export enum UrlStructure {
  /** URL structure as a subdirectory. For example, `"www.mysite.com/fr"`. */
  SUBDIRECTORY = 'SUBDIRECTORY',
  /** URL structure as a subdomain. For example, `"fr.mysite.com"`. */
  SUBDOMAIN = 'SUBDOMAIN',
  /** URL structure as a query parameter. For example, `"www.mysite.com?lang=fr"`. */
  QUERY_PARAM = 'QUERY_PARAM',
}

/** @enumType */
export type UrlStructureWithLiterals =
  | UrlStructure
  | 'SUBDIRECTORY'
  | 'SUBDOMAIN'
  | 'QUERY_PARAM';

/** Message for overriding the default resolution method. */
export interface UrlStructureOverride {
  /** URL structure to use instead of the default. */
  urlStructure?: UrlStructureWithLiterals;
}

/** Payload for the change main locale action. */
export interface CreateNewPrimaryLocalePayload {
  /** The new primary locale after the Create New Primary Locale endpoint is finished. */
  primaryLocale?: V2Locale;
}

export interface RawCreateLocaleRequest {
  /** Locale to create. */
  locale?: V2Locale;
  /** override the user primary locale value */
  userPrimary?: boolean | null;
  /** override the visitor primary locale value */
  visitorPrimary?: boolean | null;
}

export interface RawCreateLocaleResponse {
  /** created locale */
  locale?: V2Locale;
}

export interface ChangeMainLocaleRequest {
  /** the new main locale */
  locale?: V2Locale;
}

export interface ChangeMainLocaleResponse {
  /** the new main locale */
  locale?: V2Locale;
}

export interface ForceDeleteLocaleRequest {
  /**
   * The ID of the locale to retrieve.
   * @maxLength 20
   */
  localeId?: string;
}

export interface ForceDeleteLocaleResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** Request message for listing supported locales */
export interface ListSupportedLocalesRequest {
  /**
   * Language to filter by. For example, `"en"` for all English locales.
   * @format LANGUAGE
   */
  languageCode?: string | null;
  /** Whether to include all possible regional variants for each language code in the response. When `false`, only the default locale, such as `"en-US"`, is returned and regional variants like `"en-GB"` and `"en-AU"` are excluded. */
  includeAllLocales?: boolean | null;
  /** Whether to include region-specific options such as flags and regional codes for each locale in the response. */
  includeRegionOptions?: boolean | null;
}

/** Response message for listing supported locales */
export interface ListSupportedLocalesResponse {
  /** A list of all the locales that can be added to a site. */
  supportedLocales?: SupportedLocale[];
}

/** Represents a supported locale in the Wix platform */
export interface SupportedLocale {
  /** @format LANGUAGE_TAG */
  id?: string | null;
  /**
   * Language code.
   * @format LANGUAGE
   */
  languageCode?: string | null;
  /**
   * Region code.
   * @format COUNTRY
   */
  regionCode?: string | null;
  /**
   * Default flag code for the locale.
   * @maxLength 3
   */
  defaultFlag?: string | null;
  /**
   * Default format for how to display data types such as dates, times, numbers, and currencies.
   * @format LANGUAGE_TAG
   */
  defaultRegionalFormat?: string | null;
  /**
   * Language code used when content is sent for machine translation.
   * @format LANGUAGE_TAG
   */
  machineTranslationCode?: string | null;
  /**
   * Display name of the locale.
   * @maxLength 30
   */
  displayName?: string | null;
  /**
   * All flags and their regional formats per locale.
   * @maxSize 100
   */
  regionOptions?: RegionOptions[];
}

/** Represents variations of a locale with different flags and regional formats */
export interface RegionOptions {
  /**
   * Flag code of the region.
   * @maxLength 3
   */
  flag?: string | null;
  /**
   * Format for how to display data types such as dates, times, numbers, and currencies.
   * @format LANGUAGE_TAG
   */
  regionalFormat?: string | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

/** Request message for creating a new Locale. */
export interface CreateLocaleRequest {
  /** Locale to create. */
  locale: V2Locale;
}

/** Response message for creating a new Locale. */
export interface CreateLocaleResponse {
  /** The created locale. */
  locale?: V2Locale;
}

/** Request message for retrieving a Locale by ID. */
export interface GetLocaleRequest {
  /**
   * ID of the locale to retrieve.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  localeId: string;
}

/** Response message for retrieving a Locale. */
export interface GetLocaleResponse {
  /** The requested locale. */
  locale?: V2Locale;
}

/** Request message for updating a Locale. */
export interface UpdateLocaleRequest {
  /** Locale to update, may be partial. */
  locale: V2Locale;
}

/** Response message for updating a Locale. */
export interface UpdateLocaleResponse {
  /** The updated locale. */
  locale?: V2Locale;
}

/** Request message for deleting a Locale. */
export interface DeleteLocaleRequest {
  /**
   * ID of the locale to delete.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  localeId: string;
}

/** Response message for deleting a Locale. */
export interface DeleteLocaleResponse {}

/** Request message for querying Locales. */
export interface QueryLocalesRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

/** Response message for querying Locales. */
export interface QueryLocalesResponse {
  /** Retrieved locales. */
  locales?: V2Locale[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

/** This is the preferred message for cursor-paging enabled services */
export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

/** Request message for bulk creating Locales. */
export interface BulkCreateLocalesRequest {
  /**
   * Locales to create.
   * @minSize 1
   * @maxSize 100
   */
  locales: V2Locale[];
  /** Whether to receive the created locales in the response. */
  returnEntity?: boolean;
}

/** Response message for bulk creating Locales. */
export interface BulkCreateLocalesResponse {
  /**
   * Information about the created locales.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkLocaleResult[];
  /** Bulk metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkLocaleResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Created locale. Returned when `returnEntity` is set to `true`. */
  item?: V2Locale;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

/** Request message for bulk updating Locales. */
export interface BulkUpdateLocalesRequest {
  /**
   * Locales to update.
   * @minSize 1
   * @maxSize 100
   */
  locales: MaskedLocale[];
  /** Whether to receive the updated locales in the response. */
  returnEntity?: boolean;
}

export interface MaskedLocale {
  /** Locales to update, may be partial. */
  locale?: V2Locale;
}

/** Response message for bulk updating Locales. */
export interface BulkUpdateLocalesResponse {
  /**
   * Information about the updated locales.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateLocalesResponseBulkLocaleResult[];
  /** Bulk metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateLocalesResponseBulkLocaleResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated locale. Returned when `returnEntity` is set to `true`. */
  item?: V2Locale;
}

/** Request message for bulk deleting Locales. */
export interface BulkDeleteLocalesRequest {
  /**
   * IDs of locales to delete.
   * @minSize 1
   * @maxSize 100
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  localeIds: string[];
}

/** Response message for bulk deleting Locales. */
export interface BulkDeleteLocalesResponse {
  /**
   * Information about the deleted locales.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteLocalesResponseBulkLocaleResult[];
  /** Bulk metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteLocalesResponseBulkLocaleResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
}

/** Request message for changing the main locale. */
export interface CreateNewPrimaryLocaleRequest {
  /** New primary locale. */
  primaryLocale: V2Locale;
}

/** Response message for changing the main locale. */
export interface CreateNewPrimaryLocaleResponse {
  /** @format GUID */
  token?: string;
}

/** Request message for getting the status of the change main locale process. */
export interface GetNewPrimaryLocaleStatusRequest {
  /**
   * Token to retrieve the process status. Returned in the response of Create New Primary Locale.
   * @format GUID
   */
  token: string;
}

/** Response message for getting the status of the change main locale process. */
export interface GetNewPrimaryLocaleStatusResponse
  extends GetNewPrimaryLocaleStatusResponseDataOneOf {
  /** New primary locale data when the process successfully completes. */
  finishSuccessfulData?: FinishSuccessfulData;
  /** Error details when the process fails. */
  finishWithErrorData?: FinishWithErrorData;
  /** The current status of the Create New Primary Locale method. */
  status?: StatusWithLiterals;
}

/** @oneof */
export interface GetNewPrimaryLocaleStatusResponseDataOneOf {
  /** New primary locale data when the process successfully completes. */
  finishSuccessfulData?: FinishSuccessfulData;
  /** Error details when the process fails. */
  finishWithErrorData?: FinishWithErrorData;
}

export enum Status {
  /** Indicates the process of creating a new primary locale is in progress. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Indicates the process of creating a new primary locale was successful and all content under the previous primary locale is now tagged under the new locale. New primary locale data is available in the `finishSuccessfulData` object. */
  FINISH_SUCCESSFUL = 'FINISH_SUCCESSFUL',
  /** Indicates the process of creating a new primary locale failed. Additional error details are available in the `finishWithErrorData` object. */
  FINISH_WITH_ERROR = 'FINISH_WITH_ERROR',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'IN_PROGRESS'
  | 'FINISH_SUCCESSFUL'
  | 'FINISH_WITH_ERROR';

export interface FinishSuccessfulData {
  /** The new primary locale. */
  newPrimaryLocale?: V2Locale;
}

export interface FinishWithErrorData {
  /** Error details. */
  error?: ApplicationError;
}

/** Request message for setting a visitor primary locale. */
export interface SetVisitorPrimaryLocaleRequest {
  /**
   * ID of the locale to set as visitor primary.
   * @format LANGUAGE_TAG
   */
  localeId: string;
}

/** Response message for setting a visitor primary locale. */
export interface SetVisitorPrimaryLocaleResponse {
  /** Updated locale that is now set as the visitor primary locale. */
  locale?: V2Locale;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

/** Encapsulates all changes when site properties are updated */
export interface SitePropertiesUpdated {
  /** ID of the Wix site that has been updated. */
  metasiteId?: string;
  /** Update details. */
  event?: SitePropertiesEvent;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

export interface DomainChangedEvent extends DomainChangedEventPayloadOneOf {
  /** WARNING: `registered` event payload is DEPRECATED */
  registered?: Registered;
  /** WARNING: `registration_cancelled` event payload is DEPRECATED */
  registrationCancelled?: RegistrationCancelled;
  connectedToSite?: ConnectedToSite;
  disconnectedFromSite?: DisconnectedFromSite;
  /** WARNING: `registration_extended` event payload is DEPRECATED */
  registrationExtended?: RegistrationExtended;
  /** WARNING: `owner_changed` event payload is DEPRECATED */
  ownerChanged?: OwnerChanged;
  /** WARNING: `privacy_changed` event payload is DEPRECATED */
  privacyChanged?: PrivacyChanged;
  /** WARNING: `management_type_changed` event payload is DEPRECATED */
  managementTypeChanged?: ManagementTypeChanged;
  /** WARNING: `billing_subscription_cancelled` event payload is DEPRECATED */
  billingSubscriptionCancelled?: BillingSubscriptionCancelled;
  /** WARNING: `connection_verified` event payload is DEPRECATED */
  connectionVerified?: ConnectionVerified;
  /** WARNING: `connection_lost` event payload is DEPRECATED */
  connectionLost?: ConnectionLost;
  /** WARNING: `expiration_date_changed` event payload is DEPRECATED */
  expirationDateChanged?: ExpirationDateChanged;
  propagationStarted?: PropagationStarted;
  verificationStarted?: VerificationStarted;
  contactVerificationStatusChanged?: ContactVerificationStatusChanged;
  /** Expiry/GraceStarted */
  expired?: Expired;
  /** GraceEnded/RedemptionStarted */
  graceEnded?: GraceEnded;
  /** RedemptionEnded */
  redemptionEnded?: RedemptionEnded;
  /** enable or disable dnssec */
  dnssecChanged?: DnssecChanged;
  /** domain SKU updated due to price increase */
  priceIncreaseProcessed?: PriceIncreaseProcessed;
  domainName?: string;
  changeTime?: Date | null;
}

/** @oneof */
export interface DomainChangedEventPayloadOneOf {
  /** WARNING: `registered` event payload is DEPRECATED */
  registered?: Registered;
  /** WARNING: `registration_cancelled` event payload is DEPRECATED */
  registrationCancelled?: RegistrationCancelled;
  connectedToSite?: ConnectedToSite;
  disconnectedFromSite?: DisconnectedFromSite;
  /** WARNING: `registration_extended` event payload is DEPRECATED */
  registrationExtended?: RegistrationExtended;
  /** WARNING: `owner_changed` event payload is DEPRECATED */
  ownerChanged?: OwnerChanged;
  /** WARNING: `privacy_changed` event payload is DEPRECATED */
  privacyChanged?: PrivacyChanged;
  /** WARNING: `management_type_changed` event payload is DEPRECATED */
  managementTypeChanged?: ManagementTypeChanged;
  /** WARNING: `billing_subscription_cancelled` event payload is DEPRECATED */
  billingSubscriptionCancelled?: BillingSubscriptionCancelled;
  /** WARNING: `connection_verified` event payload is DEPRECATED */
  connectionVerified?: ConnectionVerified;
  /** WARNING: `connection_lost` event payload is DEPRECATED */
  connectionLost?: ConnectionLost;
  /** WARNING: `expiration_date_changed` event payload is DEPRECATED */
  expirationDateChanged?: ExpirationDateChanged;
  propagationStarted?: PropagationStarted;
  verificationStarted?: VerificationStarted;
  contactVerificationStatusChanged?: ContactVerificationStatusChanged;
  /** Expiry/GraceStarted */
  expired?: Expired;
  /** GraceEnded/RedemptionStarted */
  graceEnded?: GraceEnded;
  /** RedemptionEnded */
  redemptionEnded?: RedemptionEnded;
  /** enable or disable dnssec */
  dnssecChanged?: DnssecChanged;
  /** domain SKU updated due to price increase */
  priceIncreaseProcessed?: PriceIncreaseProcessed;
}

export interface Registered {
  /** @format GUID */
  registrationId?: string;
}

export interface RegistrationCancelled {
  /** @format GUID */
  registrationId?: string;
}

export interface ConnectedToSite {
  /** @format GUID */
  msid?: string;
}

export interface DisconnectedFromSite {
  /** @format GUID */
  msid?: string;
}

export interface RegistrationExtended {
  /** @format GUID */
  registrationId?: string;
}

export interface OwnerChanged {
  /** @format GUID */
  oldUserGuid?: string;
  /** @format GUID */
  newUserGuid?: string;
  /** @format GUID */
  oldRegistrationId?: string | null;
  /** @format GUID */
  newRegistrationId?: string | null;
}

export interface PrivacyChanged {
  /** @format GUID */
  registrationId?: string;
}

export interface ManagementTypeChanged {}

export interface BillingSubscriptionCancelled {
  /** @format GUID */
  registrationId?: string;
}

export interface ConnectionVerified {}

export interface ConnectionLost {}

export interface ExpirationDateChanged {
  /** @format GUID */
  registrationId?: string;
}

export interface PropagationStarted {
  /** @format GUID */
  userGuid?: string;
}

export interface VerificationStarted {
  /** @format GUID */
  userGuid?: string;
}

export interface ContactVerificationStatusChanged {
  status?: ContactVerificationStatusWithLiterals;
}

export enum ContactVerificationStatus {
  PENDING = 'PENDING',
  DEACTIVATED = 'DEACTIVATED',
  CONFIRMED_BY_WIX = 'CONFIRMED_BY_WIX',
  CONFIRMED_BY_REGDOTCOM = 'CONFIRMED_BY_REGDOTCOM',
}

/** @enumType */
export type ContactVerificationStatusWithLiterals =
  | ContactVerificationStatus
  | 'PENDING'
  | 'DEACTIVATED'
  | 'CONFIRMED_BY_WIX'
  | 'CONFIRMED_BY_REGDOTCOM';

export interface Expired {}

export interface GraceEnded {}

export interface RedemptionEnded {}

export interface DnssecChanged {
  /**
   * unique registration id
   * @format GUID
   */
  registrationId?: string;
}

export interface PriceIncreaseProcessed {}

/** @docsIgnore */
export type CreateNewPrimaryLocaleApplicationErrors = {
  code?: 'NEW_MAIN_LOCALE_EXIST_ON_SITE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type SetVisitorPrimaryLocaleApplicationErrors = {
  code?: 'LOCALE_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
